// Counts a look at one item, at a pace that keeps the number meaning something.
//
// A view is allowed to happen more than once. Coming back to a picture a week
// later is a second look at it, and pretending otherwise would make the number
// answer a question nobody asked. What the number must not do is respond to a
// held-down arrow key or a refresh, so three rules stand between an item being
// on screen and its counter moving:
//
//   dwell     it has to have been the thing being looked at, not passed over
//   cooldown  the same browser cannot count the same item again for a while
//   daily cap and cannot count it more than a handful of times in a day
//
// All three are enforced here rather than in the function, because the cheapest
// request is the one never sent, and because a browser is the only thing that
// knows what it has already counted. That knowledge stays on the machine: a
// timestamp per item in localStorage, never sent anywhere. There is no session
// id, no cookie and nothing that identifies a person, which is why repeat views
// can be allowed at all.

// Measured, not guessed. Scrolling steadily across the gallery leaves each
// picture centred for 348ms to 399ms, so anything above that ceiling excludes a
// pass-through while still counting the moment somebody stops. Counting
// instantly would award a view to all fourteen pictures one scroll travels
// over, and would flatter whichever ones happen to sit mid-rail on everybody's
// path rather than the ones worth looking at.
const DWELL_MS = 600;
// Nobody can be stopped from pressing share twice, and there is no reason to
// try: the button should answer every press. What is stopped is the number
// moving for it. One share per item per interval, which is the same idea as
// the view cooldown and a good deal shorter, because sharing the same piece to
// two places half an hour apart is two shares in any sense worth counting.
const SHARE_COOLDOWN_MS = 10 * 60 * 1000;
const COOLDOWN_MS = 30 * 60 * 1000;
const DAILY_CAP = 5;

const ENDPOINT = '/.netlify/functions/view';
const STORE_PREFIX = 'iv:';

// Collected rather than sent one at a time. A reader crossing the gallery can
// legitimately look at several pictures, and that should be one request when
// they leave rather than one per picture while they are still reading.
const pending = new Map();
let wired = false;

const today = () => new Date().toISOString().slice(0, 10);

function readMark(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeMark(key, mark) {
  try {
    localStorage.setItem(key, JSON.stringify(mark));
  } catch {
    // Private mode, or storage full. Losing the mark means the next look counts
    // again, which is a better failure than refusing to count at all.
  }
}

function flush() {
  if (!pending.size) return;

  const events = [...pending.values()];
  pending.clear();

  const payload = JSON.stringify({ events });

  // sendBeacon survives the page being closed mid-request, which is the moment
  // this usually fires.
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
      return;
    }
  } catch {
    // Falls through to fetch.
  }

  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // A lost count is not worth surfacing to anybody.
  });
}

function wire() {
  if (wired || typeof document === 'undefined') return;
  wired = true;

  // visibilitychange rather than unload: it is the one the mobile browsers
  // actually fire when an app is switched away from, and a phone often never
  // sees an unload at all.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
}

// Queues one view if the pacing rules allow it. Returns whether it counted,
// which is only really of interest to a test.
export function countView(kind, id) {
  if (typeof window === 'undefined' || !kind || !id) return false;

  wire();

  const key = `${STORE_PREFIX}${kind}:${id}`;
  const now = Date.now();
  const day = today();
  const mark = readMark(key);

  if (mark) {
    if (now - (mark.t ?? 0) < COOLDOWN_MS) return false;
    if (mark.d === day && (mark.n ?? 0) >= DAILY_CAP) return false;
  }

  writeMark(key, {
    t: now,
    d: day,
    n: mark && mark.d === day ? (mark.n ?? 0) + 1 : 1,
  });

  pending.set(`view:${kind}:${id}`, { kind, id, metric: 'view' });
  return true;
}

// Queues a share. Returns whether it counted, which the caller ignores: the
// press is acknowledged either way, and a press that fell inside the interval
// is still a share that happened, just not a new number.
export function countShare(kind, id) {
  if (typeof window === 'undefined' || !kind || !id) return false;

  wire();

  const key = `${STORE_PREFIX}s:${kind}:${id}`;
  const now = Date.now();
  const mark = readMark(key);

  if (mark && now - (mark.t ?? 0) < SHARE_COOLDOWN_MS) return false;

  writeMark(key, { t: now });
  pending.set(`share:${kind}:${id}`, { kind, id, metric: 'share' });
  return true;
}

// 1240 reads as a number to be parsed; 1.2K reads as "a lot". Under a thousand
// stays exact, because at that size the exact figure is the interesting part.
export function formatCount(n) {
  const value = Number(n) || 0;
  if (value < 1000) return String(value);
  if (value < 1000000) {
    const k = value / 1000;
    return `${k < 10 ? k.toFixed(1).replace(/\.0$/, '') : Math.round(k)}K`;
  }
  const m = value / 1000000;
  return `${m < 10 ? m.toFixed(1).replace(/\.0$/, '') : Math.round(m)}M`;
}

// "1 views" is the sort of thing that makes a number look automatic rather than
// true, and a floor set low enough to watch the counting work is exactly when it
// shows. Only an exact one is singular: 1.2K is plural.
export function countLabel(n, word) {
  const value = Number(n) || 0;
  return `${formatCount(value)} ${value === 1 ? word : `${word}s`}`;
}

// Counts whatever the ref is holding, once it has held it long enough to mean
// somebody looked. Scrolling the gallery from one picture to the ninth changes
// this ref seven times on the way, and none of those are views.
export function useDwellView(kind, source) {
  if (typeof window === 'undefined') return;

  let timer = null;

  const stop = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  watch(
    () => toValue(source),
    (id) => {
      stop();
      if (!id) return;
      timer = setTimeout(() => countView(kind, id), DWELL_MS);
    },
    { immediate: true }
  );

  // Leaving counts as settling: a reader who closes the tab on a picture they
  // had been looking at has looked at it.
  onBeforeUnmount(() => {
    stop();
    flush();
  });
}
